import React from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp, Users, DollarSign, Target, Eye } from 'lucide-react';

export const Analytics = () => {
  const metrics = [
    { label: 'Website Visitors', value: '2,450', change: '+12%', icon: Eye, color: 'text-blue-600' },
    { label: 'Lead Conversion Rate', value: '8.3%', change: '+2.1%', icon: Target, color: 'text-green-600' },
    { label: 'Avg. Deal Size', value: '$35,217', change: '+5%', icon: DollarSign, color: 'text-purple-600' },
    { label: 'Marketing ROI', value: '247%', change: '+15%', icon: TrendingUp, color: 'text-orange-600' }
  ];

  const funnelData = [
    { stage: 'Website Visitors', count: 2450, percentage: 100 },
    { stage: 'Leads', count: 204, percentage: 8.3 },
    { stage: 'Marketing Qualified Leads', count: 61, percentage: 2.5 },
    { stage: 'Sales Qualified Leads', count: 37, percentage: 1.5 },
    { stage: 'Opportunities', count: 18, percentage: 0.7 },
    { stage: 'Customers', count: 8, percentage: 0.3 }
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Marketing Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Deep dive into marketing performance and ROI</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Card key={metric.label}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{metric.label}</CardTitle>
                  <Icon className={`h-4 w-4 ${metric.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metric.value}</div>
                  <p className="text-xs text-green-600 mt-1">{metric.change} from last month</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Marketing Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {funnelData.map((stage, index) => (
                <div key={stage.stage} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{stage.stage}</span>
                    <span className="text-gray-600">{stage.count.toLocaleString()} ({stage.percentage}%)</span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-blue-600 to-purple-600 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                        style={{ width: `${stage.percentage * 10}%`, minWidth: '60px' }}
                      >
                        {stage.count.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {index < funnelData.length - 1 && (
                    <div className="flex items-center justify-center text-xs text-gray-500">
                      ↓ {((funnelData[index + 1].count / stage.count) * 100).toFixed(1)}% conversion
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Channels</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { channel: 'Google Ads', leads: 5, cost: 750, roi: '213%' },
                  { channel: 'Organic Search', leads: 2, cost: 500, roi: '180%' },
                  { channel: 'Facebook Ads', leads: 3, cost: 450, roi: '167%' },
                  { channel: 'Referrals', leads: 2, cost: 0, roi: '∞' }
                ].map(channel => (
                  <div key={channel.channel} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div>
                      <p className="font-medium">{channel.channel}</p>
                      <p className="text-sm text-gray-600">{channel.leads} leads • ${channel.cost} spent</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{channel.roi}</p>
                      <p className="text-xs text-gray-500">ROI</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { month: 'December 2025', leads: 12, conversions: 1, spend: 1400 },
                  { month: 'November 2025', leads: 8, conversions: 1, spend: 1200 },
                  { month: 'October 2025', leads: 15, conversions: 2, spend: 1800 },
                  { month: 'September 2025', leads: 10, conversions: 1, spend: 1500 }
                ].map(month => (
                  <div key={month.month} className="border-l-4 border-blue-600 pl-4 py-2">
                    <p className="font-medium">{month.month}</p>
                    <div className="grid grid-cols-3 gap-2 text-sm text-gray-600 mt-1">
                      <div>{month.leads} leads</div>
                      <div>{month.conversions} sales</div>
                      <div>${month.spend.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};